import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Markdown from '../components/Markdown';
import { TypewriterText } from '../hooks/useTypewriter';

const API = '/api';

/**
 * Renders one runtime's normative spec (markdown) as a full page.
 * The spec is fetched from /api/runtimes/{id}/spec.md and rendered with
 * react-markdown so we don't have to hand-port each spec to JSX.
 */
function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Every published release of a runtime, newest first, with its exact files. */
function Releases({ runtime, selected }) {
  const releases = runtime.releases || [];
  if (releases.length === 0) return null;
  return (
    <section className="mb-12 divide-y divide-[#1a201b] border-y border-[#1a201b]" data-testid="runtime-releases">
      <h2 className="text-xs lowercase text-[#666666] tracking-wider font-mono">
        releases · {releases.length}
      </h2>
      {releases.map((r, i) => {
        const active = r.version === selected;
        return (
          <article
            key={r.version}
            className={`py-5 space-y-3 ${active ? '' : 'opacity-80'}`}
            data-testid={`release-${r.version}`}
          >
            <header className="flex items-baseline justify-between gap-3 flex-wrap font-mono lowercase">
              <div className="flex items-baseline gap-3 flex-wrap">
                <Link
                  to={i === 0 ? `/runtimes/${runtime.id}` : `/runtimes/${runtime.id}?version=${encodeURIComponent(r.version)}`}
                  className="text-white hover:underline decoration-[#3a413a] underline-offset-4"
                >
                  {r.version}
                </Link>
                {i === 0 && (
                  <span className="px-2 py-0.5 text-[10px] rounded border border-white/40 text-white bg-white/5">newest</span>
                )}
                <span className="px-2 py-0.5 text-[10px] rounded border border-[#3a413a] text-[#888888]">{r.trust}</span>
              </div>
              <span className="text-xs text-[#555555]">{r.released_on ? `released ${r.released_on}` : ''}</span>
            </header>
            {r.notes && <p className="text-[#aaaaaa] lowercase text-sm leading-relaxed">{r.notes}</p>}
            <ul className="space-y-1.5 text-xs font-mono">
              {r.files.map((f) => (
                <li key={f.name} className="flex items-baseline justify-between gap-3 flex-wrap">
                  <a
                    href={f.url}
                    className="text-white/80 hover:text-white underline decoration-[#333333] underline-offset-4 hover:decoration-white"
                    data-testid={`release-file-${f.name}`}
                  >
                    {f.name}
                  </a>
                  <span className="text-[#555555] lowercase">
                    {f.kind}{f.platform !== 'any' ? ` · ${f.platform}` : ''} · {formatSize(f.bytes)} ·{' '}
                    <span title={`sha256 ${f.sha256}`}>sha256 {f.sha256.slice(0, 12)}…</span>
                  </span>
                </li>
              ))}
              <li>
                <a href={r.manifest_url} className="text-[#777777] hover:text-white">runtime.json</a>
              </li>
            </ul>
          </article>
        );
      })}
    </section>
  );
}

export default function RuntimeSpec() {
  const { runtimeId } = useParams();
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version');
  const [runtime, setRuntime] = useState(null);
  const [md, setMd] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [rRes, sRes] = await Promise.all([
          fetch(`${API}/runtimes/${encodeURIComponent(runtimeId)}`),
          fetch(`${API}/runtimes/${encodeURIComponent(runtimeId)}/spec.md${version ? `?version=${encodeURIComponent(version)}` : ''}`),
        ]);
        if (!rRes.ok) throw new Error(`runtime ${runtimeId} not found`);
        const rJson = await rRes.json();
        const specText = sRes.ok ? await sRes.text() : `# spec missing for ${runtimeId}`;
        if (!alive) return;
        setRuntime(rJson);
        setMd(specText);
      } catch (e) {
        if (alive) setError(e.message || 'failed to load runtime');
      }
    }
    load();
    return () => { alive = false; };
  }, [runtimeId, version]);

  const title = runtime ? `soma ${runtime.id} · spec` : `soma ${runtimeId} · spec`;

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="mb-8">
          <Link
            to="/downloads"
            className="text-[#555555] hover:text-white transition-colors lowercase text-sm font-mono"
            data-testid="runtime-spec-back"
          >
            ← downloads
          </Link>
        </div>

        {error && (
          <div className="text-[#aa4444] font-mono lowercase">{error}</div>
        )}

        {runtime && (
          <div className="mb-8 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-5xl lg:text-6xl font-bold text-white lowercase font-mono" data-testid="runtime-spec-title">
                <TypewriterText text={`soma ${runtime.id}`} speed={70} startDelay={150} />
              </h1>
              <span
                className={`inline-block px-2 py-0.5 text-xs font-mono lowercase rounded border tracking-wide
                  ${runtime.status === 'latest'
                    ? 'border-white/40 text-white bg-white/5'
                    : 'border-[#3a413a] text-[#888888]'}`}
              >
                {runtime.status === 'latest' ? 'current' : runtime.status}
              </span>
            </div>
            <p className="text-[#aaaaaa] lowercase text-lg">
              {runtime.headline} · runtime {version || runtime.runtime_version}
            </p>
            <div className="flex gap-3 flex-wrap pt-1 text-sm font-mono lowercase items-center">
              {runtime.downloads?.dmg && (
                <a
                  href={runtime.downloads.dmg}
                  className="border border-[#383f38] px-3 py-1 text-white hover:bg-white hover:text-black transition-colors rounded"
                  data-testid="runtime-spec-download-dmg"
                >
                  download soma for mac (.dmg)
                </a>
              )}
              {runtime.downloads?.zip && (
                <a
                  href={runtime.downloads.zip}
                  className="border border-[#383f38] px-3 py-1 text-white hover:bg-white hover:text-black transition-colors rounded"
                  data-testid="runtime-spec-download-zip"
                >
                  download bundle
                </a>
              )}
              {runtime.downloads?.py && (
                <a
                  href={runtime.downloads.py}
                  className="border border-[#22281f] px-3 py-1 text-[#aaaaaa] hover:text-white hover:border-[#383f38] transition-colors rounded"
                  data-testid="runtime-spec-download-py"
                >
                  standalone .py
                </a>
              )}
              {runtime.downloads?.spec && (
                <a
                  href={runtime.downloads.spec}
                  className="border border-[#22281f] px-3 py-1 text-[#aaaaaa] hover:text-white hover:border-[#383f38] transition-colors rounded"
                  data-testid="runtime-spec-download-md"
                >
                  spec .md
                </a>
              )}
              <span className="text-[#333333]">·</span>
              <Link
                to="/upload"
                className="text-white underline decoration-[#333333] underline-offset-4 hover:decoration-white transition-colors"
                data-testid="runtime-spec-upload-cta"
              >
                trained something? upload it →
              </Link>
            </div>
          </div>
        )}

        {runtime && <Releases runtime={runtime} selected={version || runtime.runtime_version} />}

        {md && (
          <article
            className="max-w-none space-y-4 lowercase"
            data-testid="runtime-spec-markdown"
          >
            <Markdown>{md}</Markdown>
          </article>
        )}
      </div>
    </div>
  );
}
